import styles from './MultiTypeahead.module.scss';

import { useResizeObserver } from '@react-aria/utils';
import {
  Description,
  ErrorMessage,
  FilterChip,
  FilterChipList,
  Label,
  Spinner,
  type FieldProps,
} from '../../../index';
import { Icon, IconName } from '@zapier/zinnia-icons';
import { clsx } from 'clsx';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { useFilter } from 'react-aria';
import type { Key } from 'react-aria-components';
import {
  Button,
  ComboBox,
  Input,
  ListBox,
  Popover,
} from 'react-aria-components';
import { MultiTypeaheadItem } from './MultiTypeaheadItem';

type BaseItem = {
  id: Key;
  name: string;
};

export type BetaMultiTypeaheadRenderIconContext<Item> = {
  item: Item;
};

export type BetaMultiTypeaheadProps<Item extends BaseItem> = Pick<
  FieldProps,
  'errorMessage' | 'helpText' | 'label'
> & {
  /**
   * The list of items shown in the menu.
   *
   * ---
   *
   * The list is `readonly` because this component does NOT mutate the array.
   * You may pass a non-readonly array.
   */
  items: readonly Item[];

  /**
   * The currently selected items.
   *
   * This is stored separately from the items, since typeaheads may be connected
   * to search APIs, so storing only keys makes code tougher to organize.
   *
   * ---
   *
   * The list is `readonly` because this component does NOT mutate the array.
   * You may pass a non-readonly array.
   */
  selectedItems: readonly Item[];

  /** Called with the new selection whenever it changes */
  onSelectedItemsChange: (selectedItems: Item[]) => void;

  /** Called with the new input value typed by the user */
  onInputValueChange: (inputValue: string) => void;

  /** The current text value of the text input */
  inputValue: string;

  /**
   * When to show the menu.
   *
   * - **input**: menu opens when the user edits the input text.
   * - **focus**: menu opens when the user focuses the input.
   *
   * @default "focus"
   */
  menuTrigger?: 'focus' | 'input';

  /**
   * Function to render item icons.
   */
  renderIcon?: (
    context: BetaMultiTypeaheadRenderIconContext<Item>
  ) => ReactNode;

  /** Message to display when there are no matches found. */
  emptyState?: ReactNode;

  /**
   * Customize the left icon by changing the name or passing in `null` to hide
   * it. Default is `navSearch`. The inline loading spinner won't display if the
   * left icon is hidden. Set to `null` to disable the icon, since `undefined`
   * will give the default icon.
   */
  iconLeft?: IconName | null;

  /**
   * Displays an inline loading spinner in the place of the left icon if set
   * to true.
   */
  isBusy?: boolean;

  /**
   * Set to true when the component has an invalid (error) state
   */
  isInvalid?: boolean;

  /**
   * Set to true when the component should be disabled
   */
  isDisabled?: boolean;

  /**
   * Width of the menu. By default, matches width of the combobox. Note that
   * the minimum width of the dropdown is always equal to the combobox's
   * width.
   */
  menuWidth?: string;

  /** The height of the menu. */
  menuHeight?: string;

  /** Placeholder text for the input */
  placeholder?: string;

  /**
   * The size of the input.
   * @default 'large'
   */
  size?: 'compact' | 'medium' | 'large';
};

/**
 * Combines a text input, a list box inside a popover, and chips: allows users
 * to filter a list of options to items matching a query. Similar to React
 * Aria's [ComboBox](https://react-spectrum.adobe.com/react-aria/ComboBox.html)
 * component.
 */

// Ideally we would expose a much richer interface here, including the ability
// to mark items as disabled and group items together. Ideally we would pass
// through lots of props from various parts of React Aria. I think we can add
// those later. Since this component has to be very opinionated and doesn't map
// very well onto existing React Aria patterns, it's staying simple for now.
export const BetaMultiTypeahead = <Item extends BaseItem>({
  renderIcon,
  emptyState = 'No matches found. Try a different word.',
  errorMessage,
  helpText,
  iconLeft = 'navSearch',
  inputValue,
  onInputValueChange,
  isBusy = false,
  isDisabled = false,
  isInvalid = false,
  items,
  label,
  menuHeight,
  menuWidth,
  onSelectedItemsChange,
  placeholder,
  selectedItems,
  size = 'large',
  menuTrigger = 'focus',
}: BetaMultiTypeaheadProps<Item>) => {
  // TODO: This component has accessibility problems. Because it uses React Aria
  // combobox, which follows the ARIA combobox pattern, you can only select ONE
  // ITEM at a time. So we "fake" the multi-selection in a way that won't work
  // with screen readers.

  if (!items) {
    throw new Error('MultiTypeahead: missing `items`');
  }
  if (!selectedItems) {
    throw new Error('MultiTypeahead: missing `selectedItems`');
  }

  // Calculates the width of the trigger element so that the popover can be
  // the same width (unless a `menuWidth` prop is provided). RAC provides a
  // `--trigger-width` CSS custom property, but it's based on the Input
  // component. This is the the same approach that RAC takes, and the approach
  // is recommended here:
  //
  // https://github.com/adobe/react-spectrum/discussions/8509.
  //
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<string | null>(null);
  const onResize = useCallback(() => {
    if (triggerRef.current) {
      const { left, right } = triggerRef.current.getBoundingClientRect();
      setTriggerWidth(`${right - left}px`);
    }
  }, [triggerRef, setTriggerWidth]);
  useResizeObserver({ ref: triggerRef, onResize: onResize });

  // Gives preference to the `menuWidth` prop. Otherwise, falls back to the
  // trigger width calculated above.
  const realMenuWidth = menuWidth ?? triggerWidth ?? undefined;

  const allKeys = new Set(items.map((x) => x.id));
  const selectedKeys = new Set(selectedItems.map((x) => x.id));
  const filter = useFilter({ sensitivity: 'base' });
  const filteredItems = items.filter((x) => {
    return filter.contains(x.name, inputValue);
  });
  const isEmpty = items.length === 0;
  const chipListItems = selectedItems
    .filter((item) => selectedKeys.has(item.id))
    .map((item) => ({ id: item.id, title: item.name }));

  const iconSize = (() => {
    if (size === 'compact') return '16px';
    if (size === 'large') return '24px';
    // Default to medium size icon.
    return '20px';
  })();

  const showClearSelectionButton = Boolean(inputValue && !isDisabled);

  return (
    <div className={styles.root}>
      <ComboBox<Item>
        menuTrigger={menuTrigger}
        className={styles.comboBox}
        inputValue={inputValue}
        onInputChange={onInputValueChange}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        items={filteredItems}
        onOpenChange={(isOpen, _menuTrigger) => {
          if (!isOpen) {
            onInputValueChange('');
          }
        }}
        onSelectionChange={(key) => {
          if (!key) {
            return;
          }
          const newKeys = new Set(selectedKeys);
          if (newKeys.has(key)) {
            newKeys.delete(key);
          } else {
            newKeys.add(key);
          }
          onSelectedItemsChange(items.filter((x) => newKeys.has(x.id)));
        }}
        selectedKey={null}
        allowsEmptyCollection
        data-zds
        shouldFocusWrap
      >
        {label && (
          <Label
            isCompact={size === 'compact'}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
          >
            {isInvalid && (
              <Icon
                color="StatusErrorStronger"
                isBlock
                name="formXCircle"
                size={18}
              />
            )}
            {label}
          </Label>
        )}
        <div
          className={clsx(
            styles.inputWrapper,
            !iconLeft && styles.inputWrapperNoLeftIcon
          )}
          data-disabled={isDisabled ? 'true' : undefined}
          data-invalid={isInvalid ? 'true' : undefined}
          data-size={size}
          data-zds
          ref={triggerRef}
        >
          {iconLeft && (
            <div className={styles.iconLeftWrapper} data-size={size} data-zds>
              {isBusy ? <Spinner /> : <Icon name={iconLeft} size={iconSize} />}
            </div>
          )}
          <Input
            data-content={isEmpty ? 'no-content' : 'has-content'}
            className={styles.input}
            data-zds
            data-size={size}
            placeholder={placeholder}
          />
          {showClearSelectionButton && (
            <div className={styles.iconButtonWrapper} data-size={size} data-zds>
              {/* DS-3076 will replace this custom button with an DS IconButton component */}
              <Button
                aria-label="Clear selection"
                className={styles.clearButton}
                data-size={size}
                data-zds
                onPress={() => onSelectedItemsChange([])}
                // Don't inherit default RAC Button behavior from RAC ComboBox.
                slot={null}
              >
                <Icon
                  name="arrowRefresh"
                  color="StrokeStronger"
                  size={iconSize}
                />
              </Button>
            </div>
          )}
        </div>
        {isInvalid && errorMessage ? (
          <ErrorMessage isCompact={size === 'compact'}>
            {errorMessage}
          </ErrorMessage>
        ) : (
          helpText && (
            <Description isCompact={size === 'compact'}>{helpText}</Description>
          )
        )}
        <Popover
          data-content={isEmpty ? 'no-content' : 'has-content'}
          className={styles.popover}
          offset={5}
          style={{ '--menu-width': realMenuWidth }}
          data-zds
          triggerRef={triggerRef}
        >
          <ListBox<Item>
            onSelectionChange={(selection) => {
              if (selection === 'all') {
                onSelectedItemsChange([...items]);
              } else {
                onSelectedItemsChange(items.filter((x) => selection.has(x.id)));
              }
            }}
            selectedKeys={selectedKeys}
            selectionMode="multiple"
            className={clsx(styles.listBox, menuHeight && styles.customHeight)}
            renderEmptyState={() => (
              <div className={clsx(styles.listBox, styles.emptyState)} data-zds>
                {emptyState}
              </div>
            )}
            style={{ '--menu-height': menuHeight }}
            data-zds
            dependencies={[selectedKeys]}
          >
            {(item) => {
              return (
                <MultiTypeaheadItem
                  size={size}
                  id={item.id}
                  textValue={item.name}
                  isChecked={selectedKeys.has(item.id)}
                >
                  {renderIcon?.({ item })} {item.name}
                </MultiTypeaheadItem>
              );
            }}
          </ListBox>
        </Popover>
      </ComboBox>
      {chipListItems.length > 0 && (
        <FilterChipList
          aria-label="Selected items"
          variant="primary"
          // We don't actually "select" any keys from this list... we remove
          // them if you click on them.
          selectedKeys={new Set()}
          items={chipListItems}
          selectionMode="single"
          onSelectionChange={(keys) => {
            const keysToRemove = keys === 'all' ? allKeys : new Set(keys);
            onSelectedItemsChange(
              selectedItems.filter((x) => !keysToRemove.has(x.id))
            );
          }}
          onRemove={(keys) => {
            const keysToRemove = keys;
            onSelectedItemsChange(
              selectedItems.filter((x) => !keysToRemove.has(x.id))
            );
          }}
        >
          {(props) => {
            // WTF is this error complaining about here?
            // eslint-disable-next-line react/prop-types
            const item = selectedItems.find((x) => x.id === props.id);
            if (!item) {
              throw new Error(
                // WTF is this error complaining about here?
                // eslint-disable-next-line react/prop-types
                `MultiTypeahead: Couldn't find item by id: ${props.id}`
              );
            }
            return (
              <FilterChip title={item.name}>
                {renderIcon?.({ item })}
              </FilterChip>
            );
          }}
        </FilterChipList>
      )}
    </div>
  );
};
